import type { Locale } from '../i18n';

export type CityPage = {
  slug: string;
  cityKey: 'alger' | 'oran' | 'constantine' | 'setif';
  relatedServices: string[];
  relatedProjects: string[];
  locales: Record<
    Locale,
    {
      title: string;
      h1: string;
      excerpt: string;
      body: string[];
      proof: string;
      faqs: { q: string; a: string }[];
    }
  >;
};

export const CITIES: CityPage[] = [
  {
    slug: 'alger',
    cityKey: 'alger',
    relatedServices: [
      'agence-digitale-alger',
      'developpement-logiciel-algerie',
      'creation-site-web-alger',
      'application-mobile-algerie',
    ],
    relatedProjects: ['skoda-factory', 'az-fulfillment', 'cnhj'],
    locales: {
      fr: {
        title: 'Agence développement logiciel à Alger | Codex',
        h1: 'Agence de développement logiciel à Alger',
        excerpt:
          'Studio IT à Djasr Kasentina, Alger — logiciels, apps mobiles et sites web pour entreprises de la capitale et du nord.',
        body: [
          'Codex est basé à Alger. Nous travaillons avec des startups, PME, cliniques, industriels et marques qui ont besoin d’un partenaire tech local capable de livrer.',
          'Depuis notre bureau à Djasr Kasentina, nous concevons des produits digitaux, formons des équipes, et assurons le support après mise en production.',
          'Si vous cherchez une agence digitale ou un studio de développement logiciel à Alger, commencez par un échange WhatsApp ou un brief court — nous répondons avec une prochaine étape claire.',
        ],
        proof:
          'Références livrées depuis Alger : usine Škoda (logiciel métier), Ekrili, Prof Bilal, EnginsDZ, Commix.',
        faqs: [
          {
            q: 'Où êtes-vous situés à Alger ?',
            a: 'Djasr Kasentina (Jisr Qasentina), Alger — rendez-vous sur demande.',
          },
        ],
      },
      en: {
        title: 'Software Development Agency in Algiers | Codex',
        h1: 'Software development agency in Algiers',
        excerpt:
          'IT studio in Djasr Kasentina, Algiers — software, mobile apps and websites for companies in the capital and the north.',
        body: [
          'Codex is based in Algiers. We partner with startups, SMEs, clinics, industry and brands that need a local tech partner who ships.',
          'From Djasr Kasentina we design digital products, train teams, and support after go-live.',
        ],
        proof: 'Shipped from Algiers: Škoda factory software, Ekrili, Prof Bilal, EnginsDZ, Commix.',
        faqs: [
          {
            q: 'Where in Algiers?',
            a: 'Djasr Kasentina (Jisr Qasentina), Algiers — meetings on request.',
          },
        ],
      },
      ar: {
        title: 'وكالة تطوير برمجيات في الجزائر العاصمة | كودكس',
        h1: 'وكالة تطوير برمجيات في الجزائر العاصمة',
        excerpt:
          'استوديو تقني في جسر قسنطينة — برمجيات وتطبيقات ومواقع لشركات العاصمة والشمال.',
        body: [
          'مقر كودكس في الجزائر العاصمة. نعمل مع الشركات الناشئة والمؤسسات والعيادات والصناعة التي تحتاج شريكًا تقنيًا محليًا يُسلّم.',
          'من جسر قسنطينة نصمّم المنتجات وندرب الفرق وندعم بعد الإطلاق.',
        ],
        proof: 'منجزات من العاصمة: برمجيات مصنع Škoda وEkrili وProf Bilal وEnginsDZ وCommix.',
        faqs: [
          {
            q: 'أين موقعكم في العاصمة؟',
            a: 'جسر قسنطينة، الجزائر العاصمة — مواعيد عند الطلب.',
          },
        ],
      },
    },
  },
  {
    slug: 'oran',
    cityKey: 'oran',
    relatedServices: [
      'developpement-logiciel-algerie',
      'application-mobile-algerie',
      'creation-site-web-alger',
    ],
    relatedProjects: ['ekrili', 'enginsdz'],
    locales: {
      fr: {
        title: 'Développement logiciel à Oran | Codex Alger',
        h1: 'Développement logiciel et apps pour Oran',
        excerpt:
          'Partenaire tech pour les entreprises d’Oran et de l’ouest — livraison depuis Alger, collaboration à distance et déplacements selon besoin.',
        body: [
          'Oran concentre commerce, services et startups qui ont besoin d’apps et de logiciels fiables. Codex accompagne les équipes oranaises avec le même standard de delivery qu’à Alger.',
          'Nous cadrons à distance, livrons en sprints, et organisons des ateliers sur place quand le projet le justifie.',
          'Cas d’usage fréquents à Oran : marketplaces, apps de services, sites e-commerce / vitrine, et outils métier pour le retail et la logistique.',
        ],
        proof:
          'Même stack et même qualité que nos livraisons algéroises — WhatsApp et visio pour démarrer rapidement depuis Oran.',
        faqs: [
          {
            q: 'Faut-il venir à Alger ?',
            a: 'Non. La plupart des projets Oran se gèrent à distance avec des jalons clairs ; un déplacement est possible pour le kickoff ou la formation.',
          },
        ],
      },
      en: {
        title: 'Software Development for Oran | Codex',
        h1: 'Software and apps for Oran businesses',
        excerpt:
          'Tech partner for Oran and western Algeria — delivered from Algiers with remote collaboration and on-site visits when needed.',
        body: [
          'Oran companies need reliable apps and software. Codex delivers the same standard as our Algiers work.',
          'We scope remotely, ship in sprints, and visit when the project warrants it.',
        ],
        proof: 'Start fast from Oran via WhatsApp and video — same engineering bar as Algiers.',
        faqs: [
          {
            q: 'Do we need to travel to Algiers?',
            a: 'Usually no. Most Oran projects run remotely; visits for kickoff or training are available.',
          },
        ],
      },
      ar: {
        title: 'تطوير برمجيات في وهران | كودكس',
        h1: 'برمجيات وتطبيقات لشركات وهران',
        excerpt:
          'شريك تقني لوهران والغرب — تسليم من العاصمة مع تعاون عن بُعد وزيارات عند الحاجة.',
        body: [
          'شركات وهران تحتاج تطبيقات وبرمجيات موثوقة. نقدم نفس معيار التسليم كما في العاصمة.',
          'نحدد النطاق عن بُعد ونسلّم على دفعات ونزور الموقع عند الحاجة.',
        ],
        proof: 'ابدأ بسرعة من وهران عبر واتساب والفيديو — نفس مستوى الهندسة.',
        faqs: [
          {
            q: 'هل يجب السفر إلى العاصمة؟',
            a: 'غالبًا لا. معظم مشاريع وهران تُدار عن بُعد؛ الزيارات متاحة للإطلاق أو التدريب.',
          },
        ],
      },
    },
  },
  {
    slug: 'constantine',
    cityKey: 'constantine',
    relatedServices: [
      'developpement-logiciel-algerie',
      'application-mobile-algerie',
      'cybersecurite-algerie',
    ],
    relatedProjects: ['prof-bilal', 'the-doctor-bita'],
    locales: {
      fr: {
        title: 'Développement logiciel à Constantine | Codex',
        h1: 'Agence digitale & logiciels pour Constantine',
        excerpt:
          'Apps, sites et logiciels métier pour les entreprises et établissements de Constantine et de l’est algérien.',
        body: [
          'Constantine est un pôle universitaire et entrepreneurial fort. Nous aidons les équipes locales à lancer des produits digitaux sérieux — éducation, santé, services et industrie.',
          'Collaboration à distance structurée depuis Alger, avec une communication claire en français et en arabe.',
          'Nous privilégions des MVPs mesurables plutôt que des cahiers des charges interminables.',
        ],
        proof:
          'Expérience produits éducation et santé (ex. Prof Bilal, THE-DOCTOR) pertinente pour l’écosystème constantinois.',
        faqs: [
          {
            q: 'Intervenez-vous à Constantine ?',
            a: 'Oui — projets remote-first avec ateliers éventuels sur place selon le contrat.',
          },
        ],
      },
      en: {
        title: 'Software Development in Constantine | Codex',
        h1: 'Digital products for Constantine',
        excerpt:
          'Apps, websites and business software for companies and institutions in Constantine and eastern Algeria.',
        body: [
          'Constantine is a strong academic and entrepreneurial hub. We help local teams ship serious digital products.',
          'Remote-first delivery from Algiers with clear FR/AR communication.',
        ],
        proof: 'Education and health product experience relevant to Constantine’s ecosystem.',
        faqs: [
          {
            q: 'Do you work in Constantine?',
            a: 'Yes — remote-first with optional on-site workshops.',
          },
        ],
      },
      ar: {
        title: 'تطوير برمجيات في قسنطينة | كودكس',
        h1: 'منتجات رقمية لقسنطينة',
        excerpt:
          'تطبيقات ومواقع وبرمجيات أعمال لمؤسسات قسنطينة والشرق الجزائري.',
        body: [
          'قسنطينة قطب جامعي وريادي. نساعد الفرق المحلية على إطلاق منتجات رقمية جادة.',
          'تسليم عن بُعد من العاصمة بتواصل واضح بالعربية والفرنسية.',
        ],
        proof: 'خبرة في منتجات التعليم والصحة مناسبة لمنظومة قسنطينة.',
        faqs: [
          {
            q: 'هل تعملون في قسنطينة؟',
            a: 'نعم — عن بُعد أولًا مع ورش حضورية اختيارية.',
          },
        ],
      },
    },
  },
  {
    slug: 'setif',
    cityKey: 'setif',
    relatedServices: [
      'developpement-logiciel-algerie',
      'cloud-devops-algerie',
      'creation-site-web-alger',
    ],
    relatedProjects: ['enginsdz', 'skoda-factory'],
    locales: {
      fr: {
        title: 'Développement logiciel à Sétif | Codex',
        h1: 'Logiciels et digital pour Sétif',
        excerpt:
          'Partenaire tech pour le commerce, l’industrie et les services à Sétif — sites, apps et outils métier.',
        body: [
          'Sétif est un carrefour commercial et industriel. Les entreprises locales ont besoin d’outils digitaux pragmatiques : catalogues, commandes, suivi terrain, et présence web crédible.',
          'Codex livre depuis Alger avec des sprints courts et une documentation claire pour vos équipes.',
          'Nous pouvons intégrer paiements locaux, SMS, et workflows métier adaptés à votre secteur.',
        ],
        proof:
          'Approche terrain et produits B2B (ex. EnginsDZ, logiciels usine) adaptés aux réalités de Sétif.',
        faqs: [
          {
            q: 'Pouvez-vous former nos équipes à Sétif ?',
            a: 'Oui — formation à distance ou sur site selon le projet.',
          },
        ],
      },
      en: {
        title: 'Software Development in Sétif | Codex',
        h1: 'Software and digital for Sétif',
        excerpt:
          'Tech partner for commerce, industry and services in Sétif — websites, apps and business tools.',
        body: [
          'Sétif companies need pragmatic digital tools: catalogues, orders, field ops and a credible web presence.',
          'Codex ships from Algiers with short sprints and clear documentation.',
        ],
        proof: 'B2B and industrial software experience that maps to Sétif realities.',
        faqs: [
          {
            q: 'Can you train our Sétif team?',
            a: 'Yes — remote or on-site depending on the project.',
          },
        ],
      },
      ar: {
        title: 'تطوير برمجيات في سطيف | كودكس',
        h1: 'برمجيات ورقمنة لسطيف',
        excerpt:
          'شريك تقني للتجارة والصناعة والخدمات في سطيف — مواقع وتطبيقات وأدوات أعمال.',
        body: [
          'شركات سطيف تحتاج أدوات رقمية عملية: كتالوجات وطلبات ومتابعة ميدانية وحضور ويب موثوق.',
          'نسلّم من العاصمة بدفعات قصيرة وتوثيق واضح لفرقكم.',
        ],
        proof: 'خبرة B2B وبرمجيات صناعية تناسب واقع سطيف.',
        faqs: [
          {
            q: 'هل يمكن تدريب فرقنا في سطيف؟',
            a: 'نعم — عن بُعد أو حضوريًا حسب المشروع.',
          },
        ],
      },
    },
  },
];

export function getCity(slug: string) {
  return CITIES.find((c) => c.slug === slug);
}
